import { useAuth } from '@/auth/useAuth';
import { type AsesoriaIniciarResponse, asesoriasService } from '@/services/asesorias.service';
import { useCallback, useState } from 'react';

interface UseIniciarAsesoriaResult {
  data: AsesoriaIniciarResponse | null;
  isLoading: boolean;
  error: string | null;
  iniciarAsesoria: (nit: string, objetivo: string) => Promise<AsesoriaIniciarResponse | undefined>;
}

export function useIniciarAsesoria(): UseIniciarAsesoriaResult {
  const { user } = useAuth();
  const [data, setData] = useState<AsesoriaIniciarResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const iniciarAsesoria = useCallback(
    async (nit: string, objetivo: string) => {
      // Tomar el id del JWT; si no viene, usar asesor.id como respaldo
      const asesorId = user?.id || (user?.asesor ? String(user.asesor.id) : null);

      if (!asesorId) {
        setError('Usuario no autenticado');
        return;
      }

      if (!nit || !objetivo) {
        setError('Faltan parámetros requeridos');
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const nitStr = String(nit);
        const objetivoStr = String(objetivo);
        const response = await asesoriasService.iniciar(nitStr, objetivoStr);
        setData(response);
        return response;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al iniciar asesoría');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [user]
  );

  return { data, isLoading, error, iniciarAsesoria };
}
